import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command } from '@tatra/ui';
import { LogOut, Moon, Search, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { get } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { PROFILE_ITEM, visibleNav } from '../lib/nav.js';
import { applyTheme } from '../lib/theme.js';

interface SearchGroup {
  group: string;
  hits: { id: string; title: string; subtitle?: string | null; url: string }[];
}

export interface PaletteAction {
  id: string;
  label: string;
  keywords?: string;
  run: () => void;
}

/** Actions contributed by features and modules (e.g. "New lead", "Hold unit …"). */
export const paletteActions: { current: PaletteAction[] } = { current: [] };

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** Ctrl/⌘ K: search records, jump anywhere, run actions (UX §1). */
export function CommandPalette({
  open,
  onOpenChange,
  extraActions = [],
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Commands contributed by enabled extension modules. */
  extraActions?: PaletteAction[];
}) {
  const navigate = useNavigate();
  const { can, signOut } = useAuth();
  const [q, setQ] = useState('');
  const term = useDebounced(q, 200);
  const results = useQuery({
    queryKey: ['search', term],
    queryFn: () => get<SearchGroup[]>(`/search?q=${encodeURIComponent(term)}`),
    enabled: open && term.trim().length >= 2,
    staleTime: 10_000,
  });

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const go = (to: string) => {
    onOpenChange(false);
    void navigate({ to });
  };

  const item =
    'flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 text-sm text-text aria-selected:bg-surface-hover md:min-h-9';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-overlay" />
        <DialogPrimitive.Content className="fixed top-[12vh] left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-2xl">
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search records, go to a page or run an action
          </DialogPrimitive.Description>
          <Command shouldFilter={false} label="Command palette">
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="h-4 w-4 text-text-faint" aria-hidden="true" />
              <Command.Input
                value={q}
                onValueChange={setQ}
                placeholder="Search leads, units, people… or type a command"
                className="h-12 w-full bg-transparent text-sm text-text outline-none placeholder:text-text-faint"
              />
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-text-muted">
                {term.trim().length >= 2 && results.isFetching ? 'Searching…' : 'Nothing found'}
              </Command.Empty>
              {results.data?.map((g) => (
                <Command.Group
                  key={g.group}
                  heading={g.group}
                  className="mb-2 text-xs text-text-faint [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5"
                >
                  {g.hits.map((h) => (
                    <Command.Item
                      key={`${g.group}:${h.id}`}
                      value={`${g.group}:${h.id}`}
                      onSelect={() => go(h.url)}
                      className={item}
                    >
                      <span className="truncate">{h.title}</span>
                      {h.subtitle && (
                        <span className="ml-auto truncate text-xs text-text-faint">{h.subtitle}</span>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
              <Command.Group
                heading="Actions"
                className="mb-2 text-xs text-text-faint [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5"
              >
                {[...paletteActions.current, ...extraActions]
                  .filter(
                    (a) => !q || `${a.label} ${a.keywords ?? ''}`.toLowerCase().includes(q.toLowerCase()),
                  )
                  .map((a) => (
                    <Command.Item
                      key={a.id}
                      value={`action:${a.id}`}
                      onSelect={() => (onOpenChange(false), a.run())}
                      className={item}
                    >
                      {a.label}
                    </Command.Item>
                  ))}
                {(!q || 'light theme'.includes(q.toLowerCase())) && (
                  <Command.Item
                    value="theme-light"
                    onSelect={() => (applyTheme('light'), onOpenChange(false))}
                    className={item}
                  >
                    <Sun className="h-4 w-4" aria-hidden="true" /> Light theme
                  </Command.Item>
                )}
                {(!q || 'dark theme'.includes(q.toLowerCase())) && (
                  <Command.Item
                    value="theme-dark"
                    onSelect={() => (applyTheme('dark'), onOpenChange(false))}
                    className={item}
                  >
                    <Moon className="h-4 w-4" aria-hidden="true" /> Dark theme
                  </Command.Item>
                )}
                {(!q || 'sign out log out'.includes(q.toLowerCase())) && (
                  <Command.Item value="sign-out" onSelect={() => void signOut()} className={item}>
                    <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
                  </Command.Item>
                )}
              </Command.Group>
              <Command.Group
                heading="Go to"
                className="text-xs text-text-faint [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5"
              >
                {[...visibleNav(can), { ...PROFILE_ITEM, section: 'main' as const, anyOf: [] }]
                  .filter((n) => !q || n.label.toLowerCase().includes(q.toLowerCase()))
                  .map((n) => (
                    <Command.Item key={n.to} value={`go:${n.to}`} onSelect={() => go(n.to)} className={item}>
                      <n.icon className="h-4 w-4 text-text-faint" aria-hidden="true" />
                      {n.label}
                    </Command.Item>
                  ))}
              </Command.Group>
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
